import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, FlowLayout, Icon, Input } from '@/ds'
import { useAppNavigate } from '@/lib/query-params'
import { checkinService, stepErrors } from '@/services/checkin'
import type { GuestField } from '@/services/checkin'
import { useCheckinFlow } from '../CheckinFlow'
import { backLabelKey, neighbours, progressFor, stepPath } from '../flow'
import { errorsAfterGuestChange, GuestFields } from '../fields'
import type { FieldErrors } from '../fields'
import { phoneError } from '../phone'
import { useSave } from '../useSave'

const DOCUMENT_FIELDS: GuestField[] = ['identifierTypeId', 'identifier', 'supportNumber', 'name', 'surname1', 'surname2', 'bornDate']
/** En el alta manual, país, nombre y correo ya se recogieron en el paso 2. */
const MANUAL_DOCUMENT_FIELDS: GuestField[] = ['bornDate', 'identifierTypeId', 'identifier', 'supportNumber']

/**
 * Paso 3b · Revisa lo que hemos leído (CheckIn.dc.html step=3b). Campos dudosos en aviso, no en error.
 * Sin "Coincide con la reserva" (D23: la API no da datos del titular), ni caducidad ni nacionalidad
 * (E5 no los guarda; docs/backend/checkin.md). Con "Rellenar a mano" es el formulario del documento.
 * Si se eligió la captura al registrar (D33) no hubo paso 2: aquí se piden también el email y el móvil.
 */
export function Revision() {
  const { t } = useTranslation()
  const { go } = useAppNavigate()
  const { stay, draft, updateGuest, update } = useCheckinFlow()
  const [errors, setErrors] = useState<FieldErrors>({})
  const [phoneTouched, setPhoneTouched] = useState(false)
  const { saving, error, run } = useSave()
  const manual = draft.method === 'manual' || draft.method === null
  const withContact = draft.entry === 'capture'
  const askPhone = withContact && draft.persona === 'holder'
  const FIELDS: GuestField[] = manual ? MANUAL_DOCUMENT_FIELDS : withContact ? [...DOCUMENT_FIELDS, 'email'] : DOCUMENT_FIELDS
  const phoneMsg = (() => {
    if (!askPhone || !phoneTouched) return undefined
    const e = phoneError(draft.phone)
    return e === 'prefix' ? t('v2.checkin.datos.prefijo') : e === 'digits' ? t('v2.checkin.invitar.faltanCifras') : undefined
  })()
  const { prev, next } = neighbours('3b', stay, draft.persona, draft.method, draft.entry)
  const country = stay.registration.catalogs.documentCountries.find((c) => c.id === draft.docCountryId)
  const docType = country?.documentTypes.find((d) => d.id === draft.documentTypeId)
  const docLabel = docType?.scanType === 'qr' ? t('v2.legacy.stepper.collectDataForm.documentTypes.miDniQr') : [docType?.label, country?.name].filter(Boolean).join(' · ')

  const submit = async () => {
    const e = stepErrors(FIELDS, stay, draft.guest)
    setErrors(e)
    setPhoneTouched(true)
    if (Object.keys(e).length > 0 || (askPhone && phoneError(draft.phone) !== null)) return
    const ok = askPhone ? await run(() => checkinService.setPhone(stay, draft.phone.trim()).then(() => true)) : true
    if (ok) go(stepPath(next))
  }

  return (
    <FlowLayout
      progress={{ ...progressFor('3b', stay, draft.persona, draft.method, draft.entry), backLabel: t(backLabelKey(prev === '3' ? '3b' : prev)), onBack: () => go(stepPath(prev)) }}
      title={manual ? t('v2.checkin.documento.titulo') : t('v2.checkin.revision.titulo')}
      intro={manual ? undefined : t('v2.checkin.revision.intro')}
      primary={{ label: manual ? t('v2.checkin.continuar') : t('v2.checkin.revision.correcto'), onClick: () => void submit(), loading: saving }}
      footerMessage={error}
    >
      {!manual && (
        <div className="checkin__read" role="status">
          <Icon name="check-circle" size={22} />
          <span>{t('v2.checkin.revision.leido', { tipo: docLabel })}</span>
        </div>
      )}
      <GuestFields
        stay={stay}
        guest={draft.guest}
        fields={FIELDS}
        errors={errors}
        doubtful={draft.doubtful}
        onChange={(patch) => {
          updateGuest(patch)
          setErrors((current) => errorsAfterGuestChange(current, patch, stay, draft.guest))
          const touched = Object.keys(patch) as GuestField[]
          if (touched.some((f) => draft.doubtful.includes(f))) update({ doubtful: draft.doubtful.filter((f) => !touched.includes(f)) })
        }}
        onBlurField={(f) => setErrors((prevErrors) => ({ ...prevErrors, [f]: stepErrors([f], stay, draft.guest)[f] }))}
        focusRequest={manual ? `manual-document-${draft.persona}` : undefined}
      />
      {askPhone && (
        <>
          <Input label={t('v2.checkin.datos.movil')} type="tel" inputMode="tel" autoComplete="tel" value={draft.phone} onChange={(e) => update({ phone: e.target.value })} onBlur={() => setPhoneTouched(true)} {...(phoneMsg ? { error: phoneMsg } : {})} />
          <p className="checkin__note">{t(checkinService.notices.phoneReachesProperty ? 'v2.checkin.datos.movilNota' : 'v2.checkin.datos.movilNotaDispositivo')}</p>
        </>
      )}
      {!manual && (
        <Button variant="tertiary" icon="refresh-cw" fullWidth={false} onClick={() => go(stepPath('3'))}>
          {t('v2.checkin.revision.repetir')}
        </Button>
      )}
    </FlowLayout>
  )
}
